<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    icon?: string;
    title: string;
    description?: string;
    size?: "sm" | "md" | "lg";
    // Wraps the empty state in a subtle dashed card — the standard look for
    // in-project "nothing created yet" states.
    bordered?: boolean;
  }>(),
  {
    icon: "fa-solid fa-inbox",
    size: "md",
    bordered: false,
  }
);

const iconSizeClass = computed<string>(() => {
  if (props.size === "sm") return "icon-md";
  if (props.size === "lg") return "icon-xl";
  return "icon-lg";
});

const titleClass = computed<string>(() => {
  if (props.size === "sm") return "t-sh6";
  if (props.size === "lg") return "t-sh3";
  return "t-sh5";
});

const descriptionClass = computed<string>(() => {
  if (props.size === "sm") return "t-xs";
  if (props.size === "lg") return "t-body";
  return "t-small";
});

const gapClass = computed<string>(() => {
  if (props.size === "sm") return "gap-px-8";
  if (props.size === "lg") return "gap-px-16";
  return "gap-px-12";
});
</script>

<template>
  <div
    role="status"
    class="flex flex-col items-center justify-center text-center"
    :class="[
      gapClass,
      bordered ? 'rounded-16 p-px-32 mx-auto w-full max-w-md border border-dashed border-slate-300 bg-slate-50' : '',
    ]">
    <!-- Custom illustration slot -->
    <slot>
      <font-awesome-icon
        v-if="icon"
        :icon="icon"
        class="text-type-muted"
        :class="iconSizeClass" />
    </slot>

    <div
      class="flex flex-col items-center"
      :class="size === 'sm' ? 'gap-px-4' : 'gap-px-8'">
      <p
        :class="titleClass"
        class="text-type-default">
        {{ title }}
      </p>
      <p
        v-if="description"
        :class="descriptionClass"
        class="text-type-muted max-w-xs">
        {{ description }}
      </p>
    </div>

    <!-- Actions slot (CTAs, buttons) -->
    <div
      v-if="$slots.actions"
      class="gap-px-8 flex flex-wrap items-center justify-center">
      <slot name="actions" />
    </div>
  </div>
</template>
